import { Checkbox, Dialog, FormControlLabel, LinearProgress, Radio, styled } from '@mui/material'
import AvatarComponent from 'components/avatar'
import BackComponent from 'components/back'
import ButtonMobileComponent from 'components/button-mobile'
import { AssignmentIndIcon } from 'components/icons'
import InputMobileComponent from 'components/input-mobile'
import SelectMobileComponent from 'components/select-mobile'
import TextAreaComponent from 'components/textarea'
import ToastComponent from 'components/toast'
import WizardComponent from 'components/wizard'
import CarpoolDashboardLink from 'components/link-carpool-dashboard'
import Arrow from 'images/arrow_forward.png'
import React, { useEffect, useState } from 'react'
import { NavigateFunction, useNavigate } from 'react-router-dom'
import { MessageProps } from 'types/CommonTypes'
import { RoomType, UserRoomsOutputProps } from 'types/RoomTypes'
import { UsersOutputProps } from 'types/UserTypes'
import { isWeb, METERS_TO_MILES, titleCaseSchoolNameForUI } from 'utils/constants'
import { createRoom } from 'utils/rooms'
import { getCommunityUsersSortedByDistance } from 'utils/userUtils'
import { isValidGroupDescription, isValidGroupName } from 'utils/validations'

const CustomRadio = styled(Radio)(({ theme }) => ({
  height: '20px',
  marginLeft: '4px',
  color: '#A3A3A3', // Default color when not checked
  width: '16px',
  '&.Mui-checked': {
    color: '#EBD76F', // Color when checked
  },
}))

interface RoomCreateComponentProps {
  users: UsersOutputProps[] // all users
  privateRooms: UserRoomsOutputProps[] // all existing private user rooms
  communityIds: string[] // all communities
  defaultCommunityId?: string
  userId: string // current user creating/adding
  failedPhotoLinks: Set<string>
  onFailedPhotoLink: (url: string) => void
  onUpdate: (cb?: () => void) => void
}

interface CardComponentProps {
  isChecked: boolean
  avatar?: JSX.Element
  title: string
  subtitle?: string
  body?: string
  onChange: (isChecked: boolean) => void
}

const CardComponent = (props: CardComponentProps): JSX.Element => {
  const { isChecked, avatar, title, subtitle, body, onChange } = props ?? {}

  return (
    <div
      className={`
        flex flex-col my-2 p-3 rounded-lg
        transition-colors duration-150
        ${isChecked ? 'bg-[#FFFDF5]' : 'hover:bg-gray-50'}
      `}
    >
      <FormControlLabel
        className='m-0 w-full'
        componentsProps={{
          typography: {
            component: 'div',
            className: 'w-full',
          },
        }}
        control={
          <Checkbox
            data-testid='checkbox-testid'
            value={isChecked}
            onChange={(e) => onChange(e.target.checked)}
            sx={{
              strokeWidth: 1,
              '&.Mui-checked': {
                color: '#EBD76F',
              },
              '&:hover': {
                backgroundColor: 'rgba(235, 215, 111, 0.08)',
              },
            }}
          />
        }
        label={
          <div className='flex gap-3 items-start w-full'>
            {avatar && <div className='mt-0.5'>{avatar}</div>}
            <div className='flex flex-col gap-0.5'>
              <div className='flex flex-wrap items-center gap-1.5'>
                <span className='text-[#1A1A1A] text-sm font-medium leading-tight'>{title}</span>
                {subtitle && (
                  <>
                    <span className='w-px bg-gray-200 h-4' />
                    <span className='text-[#666666] text-xs font-normal'>{subtitle}</span>
                  </>
                )}
              </div>
              <span
                className={`${body ? 'text-[#FF7900] text-sm' : 'text-[#666666] text-xs'} font-normal leading-tight mt-0.5`}
              >
                {body || 'Location not set'}
              </span>
            </div>
          </div>
        }
      />
    </div>
  )
}

const RoomCreateComponent: React.FC<RoomCreateComponentProps> = ({
  users,
  privateRooms,
  communityIds,
  defaultCommunityId,
  userId,
  failedPhotoLinks,
  onFailedPhotoLink,
  onUpdate,
}) => {
  // 1. State Hooks
  const navigate: NavigateFunction = useNavigate()
  const filters: string[] = Array.from(communityIds).sort()
  const [selectedFilter, setSelectedFilter] = useState<string>(defaultCommunityId || filters[0])
  const [filteredUsers, setFilteredUsers] = useState<CardComponentProps[]>([])
  const [selectedUsers, setSelectedUsers] = useState<Set<string>>(new Set())
  const [showModal, setShowModal] = useState<boolean>(false)
  const [roomName, setRoomName] = useState<string>('')
  const [roomDescription, setRoomDescription] = useState<string>('')
  const [roomType, setRoomType] = useState<RoomType>(RoomType.GROUP)
  const [msg, setMsg] = useState<MessageProps>()
  const [isLoading, setIsLoading] = useState<string>('')

  // 2. Derived Constants

  // 3. Effect Hooks
  // Reset selected users when selectedFilter changes
  useEffect(() => {
    setSelectedUsers(new Set()) // Only resets when the filter changes
  }, [selectedFilter])
  // Recalculate filtered users when relevant dependencies change
  useEffect(() => {
    // Filter out duplicate users and sort by distance & username, filtering out my user id
    const sortedUsers = getCommunityUsersSortedByDistance(users, userId, selectedFilter)
    const cards: CardComponentProps[] = sortedUsers.map((user: UsersOutputProps & { _distance: number }) => {
      const { userId, userName, userPhotoLink, _distance } = user
      return {
        isChecked: false,
        avatar: (
          <AvatarComponent
            name={userName}
            photoLink={userPhotoLink}
            failedPhotoLinks={failedPhotoLinks}
            onFailedPhotoLink={onFailedPhotoLink}
          />
        ),
        title: userName,
        body: _distance !== Infinity ? `${Math.round(_distance * METERS_TO_MILES * 10) / 10} miles away` : '',
        onChange: (isChecked: boolean) =>
          isChecked
            ? setSelectedUsers((prev) => new Set(prev).add(userId))
            : setSelectedUsers((prev) => {
                const curr = new Set(prev)
                curr.delete(userId)
                return curr
              }),
      }
    })
    setFilteredUsers(cards)
  }, [userId, users, selectedFilter, failedPhotoLinks, onFailedPhotoLink])

  // 4. Derived State, Validations and Early Returns

  // Don't render anything until we've initialized the selected filter and have users data
  if (!selectedFilter || isLoading) return <LinearProgress />

  // 5. Handlers
  const handleContinue = async () => {
    if (selectedUsers.size === 1) {
      // Redirerct to existing private chat room if they exist
      const roomId: string | undefined = privateRooms.find((v) =>
        v.roomMembers?.includes(Array.from(selectedUsers)[0]),
      )?.roomId
      if (roomId) navigate(`/messaging/${roomId}`, { replace: true })
      else {
        // Create private chat room
        const { msg, data, error } = await createRoom({
          roomType: RoomType.PRIVATE,
          roomUsers: [...Array.from(selectedUsers), userId],
        })
        if (error) setMsg(msg)
        else if (data) {
          await onUpdate(() => navigate(`/messaging/${data.roomId}`, { replace: true }))
        }
      }
    } else {
      setShowModal(true)
    }
  }
  const handleCreateRoom = async () => {
    setIsLoading('Creating room...')
    // Create group rooms
    const { msg, data, error } = await createRoom({
      roomType,
      roomUsers: [...Array.from(selectedUsers), userId],
      roomName,
      roomDescription,
      roomCommunityId: selectedFilter,
    })
    if (error) setMsg(msg)
    else if (data) {
      await onUpdate(() => navigate(`/messaging/${data.roomId}`, { replace: true }))
    }
    setIsLoading('')
    setShowModal(false)
  }

  // 6. Render
  const body: JSX.Element = (
    <div className={`flex flex-col flex-1 self-stretch gap-2 ${isWeb ? '' : 'px-5'}`}>
      {/***************** Select Community *****************/}
      {filters.length > 1 ? (
        <SelectMobileComponent
          isRequired={true}
          label='Select School'
          options={filters.map((v) => ({
            label: titleCaseSchoolNameForUI(v),
            value: v,
          }))}
          value={selectedFilter}
          onChange={(value: string) => setSelectedFilter(value)}
        />
      ) : (
        <div className='mb-2'>
          <div className='text-sm font-medium text-gray-600 mb-1'>Your School</div>
          <div className='flex items-center h-12 px-4 py-2 bg-white border border-gray-200 rounded-lg text-gray-800'>
            {titleCaseSchoolNameForUI(selectedFilter)}
          </div>
        </div>
      )}
      {/* **************** User List **************** */}
      {filteredUsers.length > 0 ? (
        <>
          <CarpoolDashboardLink
            userId={userId}
            communityId={selectedFilter}
            users={users}
            message='Set up location & view family profiles'
            className='justify-end'
          />
          {filteredUsers.map((u: CardComponentProps, index: number) => (
            <CardComponent key={index} {...u} />
          ))}
        </>
      ) : (
        <div className='flex flex-col items-center justify-center p-8 text-center'>
          <AssignmentIndIcon size={64} color='gray' />
          <h2 className='text-2xl font-semibold mb-2'>No Users Available</h2>
          <p className='text-gray-600 mb-6'>Check back later as more families join.</p>
        </div>
      )}
      {/* **************** Modal **************** */}
      <Dialog
        open={showModal}
        onClose={() => setShowModal(false)}
        scroll='paper'
        PaperProps={{ sx: { borderRadius: '12px', padding: '24px' } }}
      >
        <div className='flex flex-col gap-4 w-full max-w-md mx-auto p-4' data-testid='dialog-testid'>
          <div className='flex flex-col items-center text-center gap-1 mb-2'>
            <h3 className='text-xl font-semibold text-gray-900'>Create New Group</h3>
            <p className='text-sm text-gray-600'>Name your group and set who can join</p>
          </div>

          <div className='space-y-4'>
            <InputMobileComponent
              type='text'
              labeltext='Group Name'
              isRequired={true}
              isError={!isValidGroupName(roomName)}
              value={roomName}
              onChange={(v: string) => setRoomName(v)}
              data_testid='group-name-testid'
            />

            <TextAreaComponent
              labeltext='Group Description (Optional)'
              placeholder='Tell others about this group...'
              isRequired={false}
              isError={!isValidGroupDescription(roomDescription)}
              value={roomDescription}
              onChange={(v: string) => setRoomDescription(v)}
              data-testid='group-description-testid'
            />

            <div className='space-y-3 pt-2' data-testid='visibility-testid'>
              <p className='text-sm font-medium text-gray-700'>Group Type</p>

              <div className='flex items-start gap-3'>
                <div className='mt-0.5'>
                  <CustomRadio
                    data-testid='radio-private-testid'
                    size='small'
                    checked={roomType === RoomType.GROUP}
                    onChange={() => setRoomType(RoomType.GROUP)}
                    value={RoomType.GROUP}
                  />
                </div>
                <div>
                  <p className='text-sm font-medium'>Private</p>
                  <p className='text-xs text-gray-600'>Only selected users can join</p>
                </div>
              </div>

              <div className='flex items-start gap-3'>
                <div className='mt-0.5'>
                  <CustomRadio
                    data-testid='radio-public-testid'
                    size='small'
                    checked={roomType === RoomType.PUBLIC}
                    onChange={() => setRoomType(RoomType.PUBLIC)}
                    value={RoomType.PUBLIC}
                  />
                </div>
                <div>
                  <p className='text-sm font-medium'>Public</p>
                  <p className='text-xs text-gray-600'>Anyone in {titleCaseSchoolNameForUI(selectedFilter)} can join</p>
                </div>
              </div>
            </div>
          </div>

          <div className='flex gap-3 justify-end pt-4 border-t border-gray-100 mt-4'>
            <ButtonMobileComponent
              text='Cancel'
              width='w-[120px]'
              padx='px-[12px]'
              height='h-[40px]'
              color='bg-custom-gray'
              onClick={() => setShowModal(false)}
            />
            <ButtonMobileComponent
              text='Create'
              width='w-[120px]'
              padx='px-[12px]'
              height='h-[40px]'
              disabled={!isValidGroupName(roomName, true) || !isValidGroupDescription(roomDescription)}
              onClick={handleCreateRoom}
              data-testid='submit-btn-testid'
            />
          </div>
        </div>
      </Dialog>
    </div>
  )

  // Filtered Rooms
  return (
    <div className='flex flex-col min-h-screen'>
      {/***************** Display progress and error *****************/}
      {isLoading && <LinearProgress />}
      {msg && (
        <ToastComponent style={msg?.style} heading={msg?.heading} text={msg?.text} onClose={() => setMsg(undefined)} />
      )}
      {/***************** Mobile *****************/}
      {!isWeb ? (
        <div className='flex flex-col flex-1'>
          <div className='sticky top-0 z-10 bg-white'>
            <BackComponent text='Create Group' />
          </div>
          <div className='flex-1 pb-24'>{body}</div>
          <div className='fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 pt-3 pb-7 px-5'>
            <ButtonMobileComponent
              text='Continue'
              data_testid='next-btn-testid'
              color='bg-custom-yellow'
              width='w-full'
              disabled={!selectedUsers?.size}
              onClick={handleContinue}
              iconPosition='right'
              icon={Arrow}
            />
          </div>
        </div>
      ) : (
        <WizardComponent
          btnText='Next'
          onCancel={() => navigate(-1)}
          onClick={handleContinue}
          title='Create Group'
          data_testId='next-btn-testid'
          disable={!selectedUsers?.size}
        >
          {body}
        </WizardComponent>
      )}
    </div>
  )
}

export default RoomCreateComponent
